import { javascript } from '@codemirror/lang-javascript';
import { EditorView, keymap } from '@codemirror/view';
import CodeMirror from '@uiw/react-codemirror';
import parserBabel from 'prettier/parser-babel';
// Prettier
import prettier from 'prettier/standalone';
import React, { useEffect, useRef, useState } from 'react';
import { BiCodeAlt } from 'react-icons/bi';

import { useOnClickOutside } from '@/hooks/useOutsideClick';

const customLightTheme = EditorView.theme(
  {
    '&': {
      fontSize: '12px',
      backgroundColor: '#ffffff',
      color: '#000000',
      height: '100%',
      position: 'relative',
      overflow: 'auto',
    },
    '.cm-content': {
      caretColor: '#000000',
      lineHeight: '1.5',
    },
    '.cm-tooltip': {
      backgroundColor: '#ffffff',
      color: '#000000',
      border: '1px solid #d8000c',
      margin: '5px',
      zIndex: '1000',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    '.cm-tooltip-lint': {
      backgroundColor: '#ffe6e6',
      border: '1px solid #d8000c',
      padding: '8px 12px',
      borderRadius: '4px',
      fontSize: '10px',
      position: 'absolute',
      top: '100%',
      left: '0',
      marginTop: '5px',
      marginBottom: '50px',
      width: '300px',
      wordWrap: 'break-word',
      lineHeight: '1.5',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    '.cm-lint-marker-error': {
      color: '#d8000c',
      fontSize: '1.2em',
      cursor: 'pointer',
    },
    '.cm-tooltip-error': {
      background: '#d8000c',
      color: '#ffffff',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '0.875em',
    },
  },
  { dark: false }
);

interface ScenarioJSInputProps {
  contextMenuList: any[];
  value: string;
  setValue: (text: string) => void;
  placeholder: string;
  className?: string;
  isInvalid?: boolean;
}

const ScenarioJSInput = ({ ...rest }: ScenarioJSInputProps) => {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const moduleRef = useRef<HTMLDivElement | null>(null);
  const editorViewRef = useRef<EditorView | null>(null);

  useOnClickOutside(moduleRef, () => {
    setShowContextMenu(false);
  });

  const beautifyJS = () => {
    try {
      const formatted = prettier.format(rest.value, {
        parser: 'babel',
        plugins: [parserBabel],
        semi: true,
        singleQuote: false,
        quoteProps: 'preserve',
      });
      rest.setValue(formatted);
    } catch (e) {
      console.error('Prettier formatting error:', e);
    }
  };

  const keymaps = keymap.of([]);

  const handleChange = (value: string) => {
    rest.setValue(value);
    setShowContextMenu(false);
  };

  const onSelect = (e: any) => {
    addText(e?.variableName);
    setShowContextMenu(false);
  };

  const addText = (text: string) => {
    if (editorViewRef.current) {
      editorViewRef.current.focus();
      const { state, dispatch } = editorViewRef.current;
      const { from, to } = state.selection.main;
      dispatch(
        state.update({
          changes: { from, to, insert: text },
        })
      );
    }
  };

  useEffect(() => {
    beautifyJS();
  }, []);
  return (
    <div className="tw-relative tw-w-full tw-flex tw-flex-col tw-z-1">
      <div className="tw-relative tw-flex">
        <div
          className={`tw-relative tw-flex-grow tw-overflow-x-auto tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 ${
            rest.isInvalid ? 'is-invalid' : ''
          } ${rest.className ? rest.className : ''}`}
        >
          <CodeMirror
            value={rest.value}
            height={'200px'}
            extensions={[javascript(), customLightTheme, keymaps]}
            onChange={handleChange}
            placeholder={rest.placeholder}
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              highlightActiveLine: true,
              highlightActiveLineGutter: true,
              allowMultipleSelections: false,
              lintKeymap: true,
            }}
            onCreateEditor={view => {
              editorViewRef.current = view;
              beautifyJS();
            }}
          />
        </div>
        <button
          id="btn-beautify-js"
          className="tw-absolute tw-border-[1px] tw-top-1 tw-right-2 tw-px-1 tw-py-1 tw-bg-gray-200 tw-rounded-md hover:tw-bg-gray-300 tw-transition-colors tw-flex tw-items-center tw-gap-1 shadow-md"
          onClick={beautifyJS}
          type="button"
        >
          <BiCodeAlt className="tw-w-5 tw-h-5" />
        </button>
      </div>
    </div>
  );
};

export default ScenarioJSInput;
